/**
 * Blockari's ceremony and chest one-shots, in 21 Wild.
 *
 * Adam's call: the XP ceremonies and the chests sound the SAME in every Chain
 * Games title — the same files, the same base gains, the same pitch steps, the
 * same offsets. So the six cues are vendored from Blockari's
 * `public/audio/sfx/` into ours (not hot-linked), and the tables below are
 * copied from its `audio/engine.ts` and `public/audio/manifest.json` rather
 * than re-invented here. If a number looks arbitrary it is because it is
 * theirs; change it there first.
 *
 * `BASE_GAIN` is the file's own trim from their manifest, and a step's gain
 * multiplies it, exactly as their `play()` does
 * (`g.gain.value = entry.gain * (o.gain ?? 1)`).
 *
 * They still go through this game's mixer (`SynthBus`), so ducking, the
 * compressor and voice stealing apply — a vendored file is an asset, not a
 * second audio engine.
 */

import type { MixGroup, SynthBus } from '@/audio/synth';

export type CeremonyCue =
  | 'level-up'
  | 'results-shards'
  | 'streak-4'
  | 'place-heavy'
  | 'countdown-tick'
  | 'clear-1';

/**
 * Each cue's own trim, from Blockari's manifest.
 *
 * `place-heavy` is 0 there, which would mute it — the chest's landing thump
 * plays through `entry.gain * step.gain` and would come out silent. Taken as
 * "no trim recorded" rather than "silent", because a landing with no sound is
 * not what their own table describes. Flagged to Blockari.
 */
export const BASE_GAIN: Readonly<Record<CeremonyCue, number>> = {
  'level-up': 0.4,
  'results-shards': 0.4,
  'streak-4': 0.3,
  'place-heavy': 1,
  'countdown-tick': 0.4,
  'clear-1': 0.45,
};

/** Which mix group each sits in: all of these are the loud, occasional layer. */
const GROUP: Readonly<Record<CeremonyCue, MixGroup>> = {
  'level-up': 'bonus',
  'results-shards': 'bonus',
  'streak-4': 'bonus',
  'place-heavy': 'card',
  'countdown-tick': 'ui',
  'clear-1': 'bonus',
};

/** Reverb send, as this game's own cues use it: celebration blooms, handling does not. */
const SEND: Readonly<Record<CeremonyCue, number>> = {
  'level-up': 0.3,
  'results-shards': 0.34,
  'streak-4': 0.26,
  'place-heavy': 0.08,
  'countdown-tick': 0.04,
  'clear-1': 0.3,
};

/** One scheduled hit: which cue, how loud, at what rate, and how far in. */
export interface CueStep {
  readonly cue: CeremonyCue;
  /** Multiplies the cue's own trim. */
  readonly gain: number;
  /** Playback rate: raises the pitch and shortens the cue. */
  readonly rate?: number;
  /** Seconds after the sequence's origin. */
  readonly at: number;
}

/**
 * The rank-up, from the crossing. The swap is at 1.0 s, so the shards land
 * with the face change and the chime lands with the name.
 *
 * Measured by Blockari as mixed: −24.7 LUFS, just under YOU WON's −23.6 —
 * which is the ceiling Adam approved.
 */
export const RANK_UP_SEQUENCE: readonly CueStep[] = [
  { at: 0.1, cue: 'streak-4', gain: 0.7 },
  { at: 1.02, cue: 'results-shards', gain: 0.6 },
  { at: 1.42, cue: 'level-up', gain: 0.5 },
];

/** A level inside a rank: the chime at the crossing, the shards as it lands. */
export const LEVEL_UP_SEQUENCE: readonly CueStep[] = [
  { at: 0, cue: 'level-up', gain: 1 },
  { at: 0.82, cue: 'results-shards', gain: 0.35 },
];

/** The chest screen's beats. `step` is the 0-based rung, so the climb rises. */
export type ChestBeat =
  | 'land'
  | 'upgrade'
  | 'open'
  | 'card'
  | 'collect'
  | 'arrive-beam'
  | 'arrive-land'
  | 'arrive-rattle'
  | 'arrive-stow';

export function chestCues(beat: ChestBeat, step = 0): readonly CueStep[] {
  switch (beat) {
    case 'land':
      // 420 ms in: the thump lands when the case does, not when it is thrown.
      return [{ at: 0.42, cue: 'place-heavy', gain: 0.7 }];
    case 'upgrade':
      return [
        { at: 0, cue: 'level-up', gain: 0.55, rate: 1 + step * 0.12 },
        { at: 0, cue: 'results-shards', gain: 0.35, rate: 1.1 + step * 0.08 },
      ];
    case 'open':
      return [
        { at: 0, cue: 'results-shards', gain: 0.9 },
        { at: 0.04, cue: 'streak-4', gain: 0.7 },
      ];
    case 'card':
      return [{ at: 0, cue: 'countdown-tick', gain: 0.35, rate: 1.25 + step * 0.1 }];
    case 'collect':
      return [{ at: 0, cue: 'clear-1', gain: 0.5, rate: 1.1 }];
    // The arrival, as Blockari's CHEST_CUES has it: the beam drops, the chest
    // slams onto its pedestal, the lid rattles while it waits, LATER lands it
    // in the pill. Every one under the open's pair.
    case 'arrive-beam':
      return [{ at: 0, cue: 'level-up', gain: 0.4, rate: 0.85 }];
    case 'arrive-land':
      return [
        { at: 0, cue: 'place-heavy', gain: 0.8 },
        { at: 0.03, cue: 'streak-4', gain: 0.5 },
      ];
    case 'arrive-rattle':
      return [{ at: 0, cue: 'countdown-tick', gain: 1.15, rate: 0.9 }];
    case 'arrive-stow':
      return [{ at: 0, cue: 'clear-1', gain: 0.45, rate: 1.2 }];
  }
}

/**
 * A coin landing in the pill, `step` of `total` in a stream (1-based): Blockari's `clear-1` at a low gain,
 * the rate rising a little with each coin so a stream climbs instead of repeating. A single coin (a
 * refund) is the first step. Kept well under the results' own cues (YOU WON is −23.6).
 */
export const COIN_LAND_GAIN = 0.2;
/** A lone coin (a toast's payout, a refund) has no stream to add up to, so it carries a little more. */
export const COIN_LAND_SINGLE_GAIN = 0.32;
export function coinLandCues(step: number, total: number): readonly CueStep[] {
  const i = Math.max(0, Math.min(step, Math.max(total, 1)) - 1);
  const gain = total <= 1 ? COIN_LAND_SINGLE_GAIN : COIN_LAND_GAIN;
  return [{ at: 0, cue: 'clear-1', gain, rate: Math.min(1.05 + i * 0.03, 1.4) }];
}

const CUES = Object.keys(BASE_GAIN) as CeremonyCue[];

/**
 * The decoded cues.
 *
 * Opus first, AAC second: the `.ogg` is smaller and every engine but Safari
 * takes it, and the `.m4a` is there so Safari is not silent. A cue that fails
 * both is simply never scheduled — the ceremony plays, quietly.
 */
export class CeremonyKit {
  readonly #buffers = new Map<CeremonyCue, AudioBuffer>();
  #loading: Promise<void> | null = null;

  get loaded(): number {
    return this.#buffers.size;
  }

  has(cue: CeremonyCue): boolean {
    return this.#buffers.has(cue);
  }

  load(ctx: BaseAudioContext, base = 'audio/sfx/'): Promise<void> {
    this.#loading ??= this.#loadAll(ctx, base);
    return this.#loading;
  }

  async #loadAll(ctx: BaseAudioContext, base: string): Promise<void> {
    await Promise.all(
      CUES.map(async (cue) => {
        for (const ext of ['ogg', 'm4a'] as const) {
          try {
            const res = await fetch(`${base}${cue}.${ext}`);
            if (!res.ok) continue;
            this.#buffers.set(cue, await ctx.decodeAudioData(await res.arrayBuffer()));
            return;
          } catch {
            // Try the other container; a cue that cannot be decoded is dropped.
          }
        }
        console.warn(`[audio] ceremony cue "${cue}" unavailable`);
      }),
    );
  }

  /** Schedule one step. `origin` is the sequence's start on the context clock. */
  play(bus: SynthBus, step: CueStep, origin = bus.now()): boolean {
    const buffer = this.#buffers.get(step.cue);
    if (!buffer) return false;
    const requested = step.rate ?? 1;
    const rate = Number.isFinite(requested) ? Math.max(0.05, requested) : 1;
    const when = origin + step.at;
    const channel = bus.channel(when, when + buffer.duration / rate + 0.02, {
      group: GROUP[step.cue],
      send: SEND[step.cue],
    });
    const source = bus.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;
    const gain = bus.ctx.createGain();
    gain.gain.value = BASE_GAIN[step.cue] * step.gain;
    source.connect(gain).connect(channel.input);
    channel.own(source);
    source.start(when);
    return true;
  }

  /** Schedule a whole sequence against one origin, so the offsets cannot drift. */
  sequence(bus: SynthBus, steps: readonly CueStep[], origin = bus.now()): void {
    for (const step of steps) this.play(bus, step, origin);
  }
}

/** The one kit. Loaded on the first ceremony, kept for the session. */
export const ceremonyKit = new CeremonyKit();
